import * as services from '@api';
import { goBack, bNeedRefresh } from '../navigate';
import { deleteRows } from './tableRow';
import { requiredValidate, getHoverRow, batchByAsyncKeyTask, BillStatusType } from '../utils';
import { isType } from '@src/utils';
import { PageStatusType } from '@stores/pagestore';

/**
 * 批量删除
 * @param rootStore
 * @param options {
 *  // 必填字段
 *  dataSourceAlias: string
 *  // 选填字段
 *  billNo: string， 默认值：params.billNo
 * }
 * @returns
 * @hooks {
 *  // 专有钩子
 *  beforeDeleteHook: 删除请求前钩子
 *  afterDeleteHook: 删除请求后钩子
 *  // 公共钩子
 *  beforeHook: 开始钩子
 *  afterHook: 结束钩子
 *  catchHook: 异常钩子
 *  finallyHook: 最终钩子
 * }
 */
export async function batchDelete(rootStore: any, options: any = {}) {
  requiredValidate.call(this, options, ['dataSourceAlias']);
  let { dataSourceAlias = '', data = [], isAsync = false } = options;
  const { pageStore = {}, utils = {} } = rootStore;
  const { params = {}, query = {} } = pageStore;
  try {
    pageStore?.startLoading?.();
    let deleteData = {
      params: {
        ...query,
        datasource: dataSourceAlias,
        billnum: options.billNo || params.billNo,
        busiObj: options.busiObj || query.busiObj
      },
      data: {
        data: JSON.stringify(data)
      }
    }
    if (isAsync) {
      Object.assign(deleteData.data, {
        externalData: {
          isAsync: true
        }
      })
    }
    // beforeDeleteHook
    if (isType(this.beforeDeleteHook, 'Function')) {
      deleteData = await this.beforeDeleteHook(rootStore, deleteData);
    }
    // batchdelete
    let resStep1 = await services.batchdelete(deleteData);

    if (isAsync) {
      batchByAsyncKeyTask.call(this, rootStore, { ...resStep1.data.infos[0], title: lang.templateByUuid('UID:P_CORE-YNF-FE_18951C6405B80035', '删除', undefined, { returnStr: true }) });
      return resStep1;
    } else {
      // afterDeleteHook
      if (isType(this.afterDeleteHook, 'Function')) {
        resStep1 = await this.afterDeleteHook(rootStore, resStep1);
      }
      utils.message.success(lang.templateByUuid('UID:P_CORE-YNF-FE_18A243FC05A80010', '删除成功'));
      return resStep1;
    }
  } catch (err: any) {
    utils.message.danger(err?.message);
    return Promise.reject(err);
  } finally {
    pageStore?.endLoading?.();
  }
}

/**
 * 单例删除
 * @param rootStore
 * @param options {
 *  // 必填字段
 *  dataSourceAlias: string
 *  // 选填字段
 *  billNo: string， 默认值：params.billNo
 * }
 * @returns
 * @hooks {
 *  // 专有钩子
 *  beforeDeleteHook: 删除请求前钩子
 *  afterDeleteHook: 删除请求后钩子
 *  // 公共钩子
 *  beforeHook: 开始钩子
 *  afterHook: 结束钩子
 *  catchHook: 异常钩子
 *  finallyHook: 最终钩子
 * }
 */
export async function singleDelete(rootStore: any, options: any = {}) {
  requiredValidate.call(this, options, ['dataSourceAlias']);
  const { dataSourceAlias = '', data = [] } = options;
  const { pageStore = {}, utils = {} } = rootStore;
  const { params = {}, query = {} } = pageStore;
  try {
    pageStore?.startLoading?.();
    let deleteData = {
      params: {
        ...query,
        datasource: dataSourceAlias,
        busiObj: options.busiObj || query.busiObj,
        billnum: options.billNo || params.billNo
      },
      data: {
        data: JSON.stringify(data)
      }
    }
    // beforeDeleteHook
    if (isType(this.beforeDeleteHook, 'Function')) {
      deleteData = await this.beforeDeleteHook(rootStore, deleteData);
    }
    // singleDelete
    let res = await services.singleDelete(deleteData);
    // afterDeleteHook
    if (isType(this.afterDeleteHook, 'Function')) {
      res = await this.afterDeleteHook(rootStore, res);
    }
    utils.message.success(lang.templateByUuid('UID:P_CORE-YNF-FE_18A243FC05A80010', '删除成功'));
    return res;
  } catch (err: any) {
    utils.message.danger(err?.message);
    return Promise.reject(err);
  } finally {
    pageStore?.endLoading?.();
  }
}

/**
 * 删除实例
 * @param rootStore
 * @param options {
 *  // 必填字段
 *  cardStoreKey: string， 默认值：cardStore
 *  // 必填字段[树卡]
 *  // 选填字段
 *  treeStoreKey: string， 默认值：treeStore
 *  dataSourceAlias: string， 默认值：cardStore.dataSource
 *  billNo: string， 默认值：params.billNo
 * }
 * @returns
 * @hooks {
 *  // 专有钩子
 *  beforeDeleteHook: 删除请求前钩子
 *  afterDeleteHook: 删除请求后钩子
 *  // 公共钩子
 *  beforeHook: 开始钩子
 *  afterHook: 结束钩子
 *  catchHook: 异常钩子
 *  finallyHook: 最终钩子
 * }
 */
export const deleteIns = {
  afterHook(rootStore: any, data: any) {
    const { treeStoreKey = 'treeStore', cardStoreKey = 'cardStore' } = this.params;
    const treeStore = rootStore.get(treeStoreKey);
    const cardStore = rootStore.get(cardStoreKey);
    if (treeStore) {
      treeStore.delTreeNode(cardStore.getValue());
      clearTimeout(this.afterDeleteTimer);
    }
  },
  async doAction(rootStore: any, options: any = {}) {
    requiredValidate.call(this, options, ['cardStoreKey']);
    await rootStore.utils.confirm({ title: lang.templateByUuid('UID:P_CORE-YNF-FE_18951C6405B80031', '确认删除?'), content: lang.templateByUuid('UID:P_CORE-YNF-FE_18951C6405B80033', '您将删除当前单据数据'), ...options.confirmOptions });
    const { dataSourceAlias = '', cardStoreKey = 'cardStore' } = options;
    const cardStore = rootStore.get(cardStoreKey);
    if (!cardStore) throw (new Error(`The CardStore[${cardStoreKey}] not found`));
    try {
      await singleDelete.call(this, rootStore, {
        ...options,
        dataSourceAlias: dataSourceAlias || cardStore?.dataSource,
        data: [cardStore.getValue()]
      });
      this.afterDeleteTimer = setTimeout(() => {
        goBack.doAction.apply(this, arguments);
      }, 300);
    } catch (err: any) {
      return Promise.reject(err);
    }
  }
}

/**
 * 批量删除&行删除
 * @param rootStore
 * @param options {
 *  // 必填字段
 *  dataSourceAlias: string
 *  tableStoreKey: string， 默认值：tableStore
 *  // 选填字段
 *  pagingStoreKey: string，默认值：pagingStore
 *  billNo: string， 默认值：params.billNo
 * }
 * @hooks {
 *  // 专有钩子
 *  // 公共钩子
 *  beforeHook: 开始钩子
 *  afterHook: 结束钩子
 *  catchHook: 异常钩子
 *  finallyHook: 最终钩子
 * }
 */
export async function batchDeleteByRow(rootStore: any, options: any = {}) {
  requiredValidate.call(this, options, ['dataSourceAlias', 'tableStoreKey']);
  const fieldidPrefix = options.fieldid || `${rootStore?.pageStore?.params?.billNo}|batchDeleteByRow`
  const [hoverRow, tableStore] = getHoverRow.apply(this, arguments);
  const { utils = {} } = rootStore;

  // 行编辑表
  if (tableStore.uiState !== PageStatusType.BROWSE || tableStore.getValue().some((v: any) => v.$readOnly === false)) {
    return await deleteRows.doAction.apply(this, arguments);
  }

  try {
    if (hoverRow) {
      // 普通表
      await rootStore.utils.confirm({ title: lang.templateByUuid('UID:P_CORE-YNF-FE_18951C6405B80031', '确认删除?'), content: lang.templateByUuid('UID:P_CORE-YNF-FE_18951C6405B80032', '您将删除当前所在行数据'), fieldidPrefix, ...options.confirmOptions });
      await batchDelete.call(this, rootStore, { ...options, data: [hoverRow.getEntityValue()] });
      bNeedRefresh.apply(this, arguments);
    } else {
      let selectRows = tableStore.getSelectedRows();
      if (!selectRows.length) {
        utils.message.warning(lang.templateByUuid('UID:P_CORE-YNF-FE_18951C6405B80034', '目前没有选中行', undefined, { returnStr: true }));
        return Promise.reject(lang.templateByUuid('UID:P_CORE-YNF-FE_18951C6405B80034', '目前没有选中行', undefined, { returnStr: true }));
      } else {
        // 普通表
        await rootStore.utils.confirm({ title: lang.templateByUuid('UID:P_CORE-YNF-FE_18951C6405B80031', '确认删除?'), content: lang.templateByUuid('UID:P_CORE-YNF-FE_18951C6405B80036', '您将删除所有选中行数据'), fieldidPrefix, ...options.confirmOptions });
        if (selectRows.length > 0) {
          await batchDelete.call(this, rootStore, { ...options, data: selectRows.map((row: any) => row.getEntityValue()), isAsync: true });
          tableStore.clearMultiplePageSelectedData();
          bNeedRefresh.apply(this, arguments);
        }
      }
    }
  } catch (err: any) {
    return Promise.reject(err);
  }
}
